package com.crowpay

import com.lightningkite.Length
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.path
import com.lightningkite.serialization.DataClassPath
import com.lightningkite.serialization.DataClassPathPartial
import com.lightningkite.serialization.SerializableProperty
import com.lightningkite.serialization.serializableProperties
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.Padding
import kotlinx.datetime.format.char
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.Contextual
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlin.time.Duration
import kotlin.time.DurationUnit
import kotlin.time.toDuration

fun Double.roundedString(precision: Int = 2): String {
    val str = toString()
    val before = str.substringBefore('.')
    val after = str.substringAfter('.', "").take(precision)

    return if (after.isEmpty() || after.all { it == '0' }) before else "$before.$after"
}

// These polymorphic classes aren't database models, so sealed classes/interfaces should be fine.
// TODO: There's probably a better way to interface this with the dirable library
@Serializable
data class CSVColumn<K> private constructor(
    private val header: String,
    val path: DataClassPathPartial<K>,
    val format: Format<@Contextual Any?> = Format.ToString
) {
    @Serializable
    sealed interface Format<in T> {
        fun appendUnits(header: String): String = header
        fun format(value: T): String?

        @Serializable
        data object ToString : Format<Any?> {
            override fun format(value: Any?): String? = value?.toString()
        }

        @Serializable
        data class Nullable<T : Any>(val format: Format<T>) : Format<T?> {
            override fun appendUnits(header: String): String = format.appendUnits(header)
            override fun format(value: T?): String? = value?.let(format::format)
        }
    }

    fun header() = format.appendUnits(header)
    fun entry(on: K) = path.getAny(on).let { format.format(it) }

    companion object {
        fun <K, T> safe(header: String, path: DataClassPath<K, T>, format: Format<T>) = CSVColumn(header, path, format as Format<Any?>)
    }
}

val <T : Any> CSVColumn.Format<T>.nullable: CSVColumn.Format<T?> get() = CSVColumn.Format.Nullable(this)

@Serializable
data class CSV<K>(
    val columns: List<CSVColumn<K>>,
    val fieldSeparator: Char = ',',
    val recordSeparator: Char = '\n',
    val quoteCharacter: Char = '"',
    val nullMarker: String = "null"
) {
    // Stolen from CsvConfig, because I'm too lazy to import.
    private fun Appendable.appendCsvEscaped(value: String) {
        if (value.contains(fieldSeparator) || value.contains(recordSeparator) || value.contains(quoteCharacter)) {
            this.append(quoteCharacter)
            this.append(value.replace("$quoteCharacter", "$quoteCharacter$quoteCharacter"))
            this.append(quoteCharacter)
        } else {
            this.append(value)
        }
    }

    private fun Appendable.row(row: List<String>) {
        var first = true
        for (value in row) {
            if (first) first = false
            else append(fieldSeparator)
            appendCsvEscaped(value)
        }
        append(recordSeparator)
    }

    operator fun invoke(data: List<K>) = buildString {
        row(columns.map { it.header() })
        for (value in data) {
            row(columns.map { it.entry(value) ?: nullMarker })
        }
    }

    class Builder<K> {
        var fieldSeparator: Char = ','
        var recordSeparator: Char = '\n'
        var quoteCharacter: Char = '"'
        var nullMarker: String = "null"

        private val columns = ArrayList<CSVColumn<K>>()
        fun <T> DataClassPath<K, T>.column(name: String, format: CSVColumn.Format<T> = CSVColumn.Format.ToString) {
            columns.add(CSVColumn.safe(name, this, format))
        }

        fun build() = CSV(columns, fieldSeparator, recordSeparator, quoteCharacter, nullMarker)
    }

    companion object {
        inline fun <reified K> build(build: Builder<K>.(DataClassPath<K, K>) -> Unit) = Builder<K>().apply { build(path()) }.build()
    }
}

@Serializable
data class RequestCSV<K>(
    val query: Query<K>,
    val csv: CSV<K>
)

object DurationFormats {
    private fun DurationUnit.shortName(): String = when (this) {
        DurationUnit.NANOSECONDS -> "ns"
        DurationUnit.MICROSECONDS -> "us"
        DurationUnit.MILLISECONDS -> "ms"
        DurationUnit.SECONDS -> "s"
        DurationUnit.MINUTES -> "m"
        DurationUnit.HOURS -> "h"
        DurationUnit.DAYS -> "d"
        else -> error("Unknown unit: $this")
    }

    private val DurationUnit.inMilliseconds get() = when (this) {
        DurationUnit.NANOSECONDS -> 1.0 / 1_000_000
        DurationUnit.MICROSECONDS -> 1.0 / 1_000
        DurationUnit.MILLISECONDS -> 1.0
        DurationUnit.SECONDS -> 1_000.0
        DurationUnit.MINUTES -> 60_000.0
        DurationUnit.HOURS -> 3_600_000.0
        DurationUnit.DAYS -> 86_400_000.0
        else -> throw IllegalArgumentException()
    }

    @Serializable
    data class NumberAsDuration(val unit: DurationUnit, val format: CSVColumn.Format<Duration>) : CSVColumn.Format<Number> {
        override fun appendUnits(header: String): String = format.appendUnits(header)
        override fun format(value: Number): String? = format.format(value.toLong().toDuration(unit))
    }

    @Serializable
    data class UnitFormat(val unit: DurationUnit) : CSVColumn.Format<Duration> {
        override fun appendUnits(header: String): String = "$header (${unit.shortName()})"
        override fun format(value: Duration): String = (value.inWholeMilliseconds / unit.inMilliseconds).roundedString()
    }

    val Seconds = UnitFormat(DurationUnit.SECONDS)
    val Minutes = UnitFormat(DurationUnit.MINUTES)
    val Hours = UnitFormat(DurationUnit.HOURS)

    @Serializable
    data object ColonFormat : CSVColumn.Format<Duration> {
        override fun appendUnits(header: String): String = "$header (h:m:s)"

        private fun Long.modAndPad() = rem(60).toString().padStart(2, '0')
        override fun format(value: Duration): String = "${value.inWholeHours}:${value.inWholeMinutes.modAndPad()}:${value.inWholeSeconds.modAndPad()}"
    }
}

object LengthFormats {
    @Serializable
    enum class LengthUnit(val meters: Double, val abbr: String) {
        Nanometers(1e-9, "nm"),
        Micrometers(1e-6, "μm"),
        Millimeters(1e-3, "mm"),
        Centimeters(1e-2, "cm"),
        Decimeters(1e-1, "dm"),
        Meters(1.0, "m"),
        Kilometers(1_000.0, "km"),
        Inches(0.0254, "in"),
        Feet(0.3048, "ft"),
        Yards(0.9144, "yd"),
        Miles(1_609.344, "mi")
    }

    fun Length.toDouble(unit: LengthUnit) = meters / unit.meters

    @Serializable
    data class UnitFormat(val unit: LengthUnit) : CSVColumn.Format<Length> {
        override fun appendUnits(header: String): String = "$header (${unit.abbr})"
        override fun format(value: Length): String? = value.toDouble(unit).roundedString()
    }

    val Meters = UnitFormat(LengthUnit.Meters)
    val Kilometers = UnitFormat(LengthUnit.Kilometers)
    val Feet = UnitFormat(LengthUnit.Feet)
    val Miles = UnitFormat(LengthUnit.Miles)
}

object LocalDateFormats {
    @Serializable
    data object MMDDYYYY : CSVColumn.Format<LocalDate> {
        val formatter = LocalDate.Format {
            monthNumber(Padding.NONE)
            char('/')
            dayOfMonth(Padding.NONE)
            char('/')
            year()
        }
        override fun format(value: LocalDate): String? = value.format(formatter)
    }
}

object LocalDateTimeFormats {
    @Serializable
    data object MMDDYYYY_HMM : CSVColumn.Format<LocalDateTime> {
        val formatter = LocalDateTime.Format {
            monthNumber(Padding.NONE)
            char('/')
            dayOfMonth(Padding.NONE)
            char('/')
            year()
            char(' ')
            amPmHour(Padding.NONE)
            char(':')
            minute(Padding.ZERO)
            char(' ')
            amPmMarker("AM", "PM")
        }
        override fun format(value: LocalDateTime): String? = value.format(formatter)
    }
}

object InstantFormats {
    @Serializable
    data class AsLocalDateTime(
        val format: CSVColumn.Format<LocalDateTime>,
        val timeZone: TimeZone = TimeZone.UTC,
        val zoneName: String? = timeZone.id
    ) : CSVColumn.Format<Instant> {
        override fun appendUnits(header: String): String = if (zoneName != null) "$header ($zoneName)" else header
        override fun format(value: Instant): String? = format.format(value.toLocalDateTime(timeZone))
    }
}